import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LayersControl, MapContainer, Polygon, WMSTileLayer, TileLayer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import { useLocation } from "react-router-dom";
import { LatLon } from 'geodesy/mgrs';
import TiffDisplay from "../TiffDisplay";
import { getLocalStorage } from '../Authentication/hooks/localStorage';
import useToken from "../Authentication/hooks/useToken";
import './OpenField.css'
import Popup from "../Popup";
import FieldCharts from "../Charts/FieldCharts";
import '../Layout.css'
import Breadcrumb from "../Breadcrumbs";
import EconomicModel from "./EconomicModel"
import Legend from "./MapLegend";
import ProcessingComponent from './Processing_dynamic';
import { useMapEvent } from "react-leaflet/hooks";
import DataStatisticsCharts from './YQdata_statistics';
import { getLayerMinMax } from './helpers';


function OpenField(props) {
    const token = useToken();
    const mapRef = useRef(null);
    const location = useLocation();
    const userID = getLocalStorage('userID');
    const dateRef = useRef();

    const farmID = location.state?.farmID;
    const farmName = location.state?.farmName;
    const fieldID = location.state?.id;
    const fieldName = location.state?.name;
    const field_lat = location.state?.fieldLat;
    const field_lon = location.state?.fieldLon;
    const coordinates = location.state?.coordinates;

    const [images, setImages] = useState(['']);
    const [YQdata, setYQdata] = useState({});
    const [show, setShow] = useState(false);
    const field = JSON.parse(coordinates);
    const [buttonPopup, setButtonPopup] = useState(false);
    const [errorMessage, setErrorMessage] = useState();
    const [processing, setProcessing] = useState(false);
    const [selectedDate, setSelectedDate] = useState();
    const [selectLayer, setSelectLayer] = useState('Yield');
    const [modelMsg, setModelMsg] = useState("");


    const breadcrumbItems = [
        { label: 'Home', link: '/' },
        { label: 'Farms', link: '/farm' },
        { label: `${farmName}`, link: `/${farmID}/fields`, state: { farmName: farmName, farmID: farmID, farmLat: field_lat, farmLon: field_lon } },
        { label: `${fieldName}`, link: `/${farmID}/field/${fieldID}` },
    ];

    const YieldQualityModel = async () => {
        const latLongP = new LatLon(field_lat, field_lon);
        const utmCoord = latLongP.toUtm();
        const mgrsGRef = utmCoord.toMgrs();
        const mgrsStr = mgrsGRef.toString();
        const baseTileID = mgrsStr.replace(" ", "").substring(0, 5);
        const tileIDs = [baseTileID];

        try {
            setProcessing(true)
            const model = await fetch('/alfalfa/yieldModel/model/', {
                method: "POST",
                body: JSON.stringify({
                    tile_id: tileIDs,
                    user_id: userID,
                    farm_id: farmID,
                    field_id: fieldID,
                    boundary: coordinates,
                    SelectedDate: selectedDate
                }),
                headers: {
                    "Content-type": "application/json",
                }
            });
            if (!model.ok) {
                throw new Error('Network response was not ok');
            }
            const payload = await model.json();
            const { source, image_path, data } = payload;
            const sourceMsgMap = {
                weather: "Result generated based on the forecast weather data (no recent satellite data).",
                s1: "Result generated only from Sentinel-1 SAR imagery collected in the last 6 days.",
                hls: "Result generated only from HLS (Harmonized Landsat–Sentinel-2) imagery.",
                ensemble: "Result generated by averaging Sentinel-1 SAR and HLS products.",
            }[source] ?? `Model source: ${source}`;

            setModelMsg(sourceMsgMap);

            const filteredImagePaths = image_path.filter(imageName => {
                return imageName.includes('gap1') && ['Yield', 'CP', 'ADF', 'NDF', 'NDFD'].some(keyword => imageName.includes(keyword));
            });
            const uniqueImagePaths = [...new Set(filteredImagePaths)];

            setProcessing(false)
            setImages(uniqueImagePaths);
            setYQdata(data);

            const savePromises = uniqueImagePaths.map(async (imageName) => {
                const save_result = await fetch(`/alfalfa/${farmID}/field/${fieldID}/images`, {
                    method: 'POST',
                    body: JSON.stringify({
                        date: '2024-10-20',
                        path: `${imageName}`,
                    }),
                    headers: {
                        "Content-type": "application/json",
                        "Authorization": 'Bearer ' + token
                    }
                });

                if (!save_result.ok) {
                    const errorData = await save_result.json();
                    console.log("Failed to save the image:", errorData.message || "Unknown error");
                    return null;
                }
                return save_result.json();
            });
            await Promise.all(savePromises);
            setShow(true)

            if (mapRef.current) {
                mapRef.current.invalidateSize(); // Refresh and re-render the map
            }
        } catch (error) {
            setErrorMessage(error)
            setButtonPopup(true)
            setProcessing(false)
        }
    };

    const PopupHandler = () => {
        return (
            <Popup trigger={buttonPopup} setTrigger={setButtonPopup}>
                <div className='error-message'>{errorMessage}</div>
            </Popup>)
    }

    const minMax = useMemo(
        () => getLayerMinMax(selectLayer, YQdata),
        [selectLayer, YQdata]
        );
    const handleBaseChange = useCallback((e) => {
        const raw = e?.name ?? e?.layer?.options?.name ?? "";
        const band = String(raw).split(",")[0].trim();
        setSelectLayer(band);
        }, []);
    const LayerEvent = () => { useMapEvent('baselayerchange', handleBaseChange); return null; };

    const rasters = useMemo(() => {
        return images.length === 0 ? [""] : images.map((img, i) => ({ url: img, band: img.split('_')[1], minMax: getLayerMinMax(img.split('_')[1], YQdata) }))
    }, [show, images, YQdata])

    const sortedRasters = useMemo(() => {
        const yieldRaster = rasters.find(r => r.band === 'Yield');
        if (!yieldRaster) return rasters;              // no Yield → original order
        return [yieldRaster, ...rasters.filter(r => r !== yieldRaster)];
    }, [rasters]);

    useEffect(() => {
        if (!sortedRasters || sortedRasters.length === 0) return;
        const bands = new Set(sortedRasters.map(r => r.band));
        if (!selectLayer || !bands.has(selectLayer)) {
            setSelectLayer(sortedRasters[0].band);
        }
        }, [sortedRasters, selectLayer]);

    const tomorrowLabel = useMemo(() => {
        const t = new Date();
        t.setDate(t.getDate() + 1);
        return t.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    }, []);

    const MapSection = useMemo(() => (
        <MapContainer ref={mapRef} bounds={field} className="layout-map">
            <LayerEvent />

            <WMSTileLayer
                attribution="USGS The National Map: Orthoimagery. Data refreshed December, 2021."
                url="https://basemap.nationalmap.gov/arcgis/services/USGSImageryOnly/MapServer/WMSServer"
                format="image/png" layers="0" transparent maxNativeZoom={16} maxZoom={24}
            />

            <LayersControl className="toggle">
                <LayersControl.Overlay checked name="Field_boundary">
                    <Polygon pathOptions={{ color: 'red' }} positions={field} />
                </LayersControl.Overlay>

                {show && sortedRasters.map((img, i) => (
                    <LayersControl.BaseLayer key={img.url} name={`${img.url.split('_')[1]}, ${tomorrowLabel}`} checked={img.band === selectLayer} >
                        <TiffDisplay url={img.url} band={img.band} minMax={img.minMax} opacity={1} />
                    </LayersControl.BaseLayer>
                ))}
            </LayersControl>
            {show && images.length > 0 && minMax && (<Legend minmax={minMax} title={selectLayer} units="" />)}
        </MapContainer>
    ), [field, images, show, minMax, selectLayer, sortedRasters, tomorrowLabel]);

    return (
        <>
            <div>
                <Breadcrumb items={breadcrumbItems} />
            </div>
            <div className="layout-container">
                {MapSection}
                <PopupHandler />
                <div className="layout-box">
                    <FieldCharts />
                    <div className="yield-input-container">
                        <div className="box-heading-yield"> Estimate Yield and Quality </div>
                        <div className="yield-quality-input" >
                            <label className="question" > When do you plan to harvest (within the next 7 days)?</label>
                            <input
                                className={`question ${selectedDate ? 'date-selected' : ''}`}
                                type="date"
                                id="dateValue"
                                ref={dateRef}
                                autoComplete="off"
                                min={new Date().toISOString().split("T")[0]}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                value={selectedDate}
                                required
                            />
                            {selectedDate ? (<button className="done-btn-YQ" onClick={YieldQualityModel} > Run Model </button>) : (<button className="not-active-button"> Run Model </button>)}
                        </div>
                        {processing && <ProcessingComponent />}
                        {show && !processing && (
                            <>
                                <div className="notification" >{modelMsg}</div> 
                                <div className="notification">
                                    Yield and quality tables are ready. Choose a map layer to visualise the results or run the economic model.
                                </div>
                            </>
                        )}
                        {show && !processing && (<DataStatisticsCharts YQdata={YQdata} />)}
                    </div>
                    <EconomicModel YQdata={YQdata} latitude={field_lat} longitude={field_lon} cuttingTime={selectedDate} />
                </div>
            </div>
        </>
    );
};

export default OpenField;